/**
 * ============================================================================
 * useKpiDashboardQuery — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the cross-supplier KPI table. Server
 *          returns one row per supplier with the 8 KPI columns + the
 *          aggregate quality score + the risk band.
 * Endpoint: GET /api/v1/suppliers/kpi-dashboard
 *
 * NOTE: this screen is consumed by Quality Directors, so we accept the
 *  workbench-style filter triplet (criticality / lifecycle / risk) to keep
 *  the cross-screen mental model consistent.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type {
  RiskCategory,
  SupplierCriticality,
  SupplierLifecycleStatus,
} from '../SupplierWorkbench/useSuppliersQuery';
import type { SupplierKpiKey } from '../SupplierDetail/useSupplierDetailQuery';

export interface KpiCellValue {
  value: number | null;
  band: 'GREEN' | 'AMBER' | 'RED' | 'UNKNOWN';
}

export interface KpiDashboardRow {
  supplier_id: string;
  name: string;
  criticality: SupplierCriticality;
  lifecycle_status: SupplierLifecycleStatus;
  risk_category: RiskCategory | null;
  aggregate_quality_score: number | null;
  kpis: Partial<Record<SupplierKpiKey, KpiCellValue>>;
}

export interface KpiDashboardResponse {
  generated_at_utc: string;
  rows: KpiDashboardRow[];
}

export interface KpiDashboardParams {
  criticality?: SupplierCriticality;
  lifecycle_status?: SupplierLifecycleStatus;
  risk_category?: RiskCategory;
}

export function useKpiDashboardQuery(
  params: KpiDashboardParams,
): UseQueryResult<KpiDashboardResponse> {
  return useQuery<KpiDashboardResponse>({
    queryKey: ['suppliers', 'kpi-dashboard', params],
    queryFn: ({ signal }) =>
      apiFetch<KpiDashboardResponse>('/api/v1/suppliers/kpi-dashboard', {
        query: {
          criticality: params.criticality,
          lifecycle_status: params.lifecycle_status,
          risk_category: params.risk_category,
        },
        signal,
      }),
  });
}
